import React, {useContext, useEffect, useMemo, useState} from "react";
import {GlobalContext} from "../../../utils/context/GlobalContextProvider.jsx";
import {useTranslation} from "react-i18next";
import {useLegacyPeriodReportError} from "../OrderDashboard/Layout/LegacyPeriodReportBoundary.jsx";
import LoadingComponent from "../../../components/LoadingComponent.jsx";
import {ProjectApi} from "../../../api/authority/generated/index";
import toast from "react-hot-toast";
import {ChannelGridSection} from "./ChannelGridSection.jsx";
import {GridType, marketingProfitabilityTotalValues, useMarketingColumns} from "./channelGridColumnSetup.js";
import {ALWAYS_LAST_KEY} from "../../../utils/dataGridSorting.js";
import updateTotal, {calculateMarketingChannelResults} from "../../../utils/datagridTotal.js";
import GridSection from "../../../components/grid/GridSection.jsx";
import Modal from "../../../components/Modal.jsx";
import {FilterContext} from "../../../components/filters/DashboardFilterContextProvider.jsx";
import FormError from "../../../components/form/FormError.jsx";
import useUserSettings from "../../../utils/useUserSettings.js";
import DataGrid2 from "../../../components/datagrid/DataGrid2.jsx";
import ReportingSnapshotStatus from "../../admin/ReportingSnapshotStatus.jsx";
import {isSnapshotPeriodIncomplete, snapshotHasPublishedDays} from "../../../utils/reportingSnapshotAvailability.js";
import {combineMediaSourceQualities} from "../../../utils/reportingMetricPresentation.js";
import {allowsCampaignBreakdown} from "../../../utils/subscriptionFeatures.js";
import ReportingCostCoverageContext from "../../../components/reporting/ReportingCostCoverageContext.jsx";

export function channelGridRows(collection) {
    return snapshotHasPublishedDays(collection?.snapshot) ? (collection?.items ?? []) : [];
}

export function channelGridShowsFullPeriodTotals(snapshot) {
    return snapshotHasPublishedDays(snapshot);
}

export function channelGridShowsPurchaseCostCoverage(gridType, metricContext) {
    return gridType === GridType.MarketingProfitability && metricContext === "marketing";
}

export function channelAllowsCampaignBreakdown(channel, campaignBreakdownAllowed) {
    return campaignBreakdownAllowed && channel?.orderOnly !== true;
}

export function createRequestLifetime() {
    let active = true;

    return {
        run(callback) {
            if (active) {
                callback();
            }
        },
        cancel() {
            active = false;
        },
    };
}

export function channelGridCampaignRequestParams({
    projectId,
    channelId,
    campaignRemoteId,
    since,
    until,
    centresAllowListed,
    domainsAllowListed,
}) {
    return {
        projectId,
        channelId,
        campaignRemoteId,
        erpFilter: {
            since,
            until,
            selectedCentreIds: centresAllowListed ?? [],
            selectedDomainIds: domainsAllowListed ?? [],
        },
    };
}

export function requestChannelCampaignData(api, request) {
    return api.marketingChannelCampaignData(channelGridCampaignRequestParams(request));
}

export function ChannelGridModalContent({data, columns}) {
    const snapshot = data?.snapshot;
    const rowsAvailable = snapshotHasPublishedDays(snapshot);
    const showTotals = channelGridShowsFullPeriodTotals(snapshot);
    const adSets = data?.adSets ?? [];

    return <>
        <ReportingSnapshotStatus snapshots={snapshot} mediaSourceQuality={snapshot?.mediaSourceQuality ?? data?.mediaSourceQuality}/>
        {rowsAvailable && data?.remoteId && <div>
            <DataGrid2
                group={'channel-grid-modal'}
                dataSource={[data]}
                columns={columns}
                showHeader={true}
                hasTotal={showTotals}
            />
            <div className="space-y-2 pt-3">
                {adSets.map((adSet) => {
                    const row = {...adSet, [ALWAYS_LAST_KEY]: true};
                    return <div
                        className="app-surface shadow-lg rounded-lg border px-5 py-4 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700"
                        key={'ad-set' + adSet.remoteId}
                    >
                        <div className="md:flex justify-between items-center space-y-4 md:space-y-0 space-x-2">
                            <div className="flex items-start space-x-3 md:space-x-4">
                                <div>
                                    <div className="inline-flex font-semibold text-slate-800 dark:text-slate-100">
                                        {adSet.name}
                                    </div>
                                    <div className="text-xs">{adSet.remoteId}</div>
                                </div>
                            </div>
                        </div>
                        <div className={'pt-2'}>
                            <DataGrid2
                                group={'channel-grid-modal'}
                                dataSource={showTotals ? [...(adSet.ads ?? []), row] : (adSet.ads ?? [])}
                                columns={columns}
                                showHeader={true}
                                hasTotal={showTotals}
                            />
                        </div>
                    </div>;
                })}
            </div>
        </div>}
    </>;
}

export default function ChannelGrid({project, gridType, collection = undefined, showSnapshotStatus = collection === undefined, metricContext = undefined}) {
    const campaignBreakdownAllowed = allowsCampaignBreakdown(project);

    let {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);

    let {getAuthorityConfiguration} = useContext(GlobalContext);

    let {t} = useTranslation();
    let {et} = useLegacyPeriodReportError();

    let [loadedCollection, setLoadedCollection] = useState(null);

    let [channelModal, setChannelModal] = useState(null);
    let [channelModalData, setChannelModalData] = useState(null);

    let [userSettings] = useUserSettings();

    useEffect(() => {
        if (collection !== undefined) {
            return;
        }

        const requestLifetime = createRequestLifetime();
        setLoadedCollection(null);

        new ProjectApi(getAuthorityConfiguration())
            .marketingChannelOverviews({
                projectId: project.id,
                erpFilter: {
                    since: since.format('YYYY-MM-DD'),
                    until: until.format('YYYY-MM-DD'),
                    selectedCentreIds: centresAllowListed ?? [],
                    selectedDomainIds: domainsAllowListed ?? [],
                },
            })
            .then((result) => {
                requestLifetime.run(() => {
                    setLoadedCollection(result);
                });
            })
            .catch((e) => {
                requestLifetime.run(() => {
                    setLoadedCollection({items: []});
                    et(e).then((message) => { if (message) toast.error(message); });
                });
            });

        return () => {
            requestLifetime.cancel();
        };
    }, [
        collection,
        project.id,
        since.format('YYYY-MM-DD'),
        until.format('YYYY-MM-DD'),
        centresAllowListed,
        domainsAllowListed,
    ]);

    let effectiveCollection = collection === undefined ? loadedCollection : collection;
    let channelList = effectiveCollection?.items ?? null;
    const snapshot = effectiveCollection?.snapshot;
    const visibleChannels = channelGridRows(effectiveCollection);
    const showFullPeriodTotals = channelGridShowsFullPeriodTotals(snapshot);


    let [channelTotal, setChannelTotal] = useState({});

    useEffect(() => {
        setChannelTotal({});
    }, [effectiveCollection]);

    let columns = useMarketingColumns(gridType, project, metricContext);

    let channelTotalResult = useMemo(() => {
        const mediaSourceQuality = effectiveCollection?.snapshot?.mediaSourceQuality
            ?? combineMediaSourceQualities(Object.values(channelTotal).map((row) => row?.mediaSourceQuality));
        let totalRow = {
            name: t('project.total'),
            mediaSourceQuality,
        };

        totalRow[ALWAYS_LAST_KEY] = true;

        let foundAny = false;

        for (let subRow of Object.values(channelTotal)) {
            for (let column of Object.values(columns)) {
                totalRow = updateTotal(totalRow, column, subRow[column.name]);
                foundAny = true;
            }
        }

        totalRow = calculateMarketingChannelResults(totalRow);

        if (metricContext === "marketing" && gridType === "MarketingProfitability") {
            Object.assign(totalRow, marketingProfitabilityTotalValues(effectiveCollection?.partialProfitabilityMarketing, mediaSourceQuality));
        }

        if (!foundAny) {
            return [];
        }

        return [totalRow];
    }, [channelTotal, effectiveCollection, gridType, metricContext, userSettings]);

    useEffect(() => {
        if (channelModal === null || !campaignBreakdownAllowed) {
            setChannelModalData(null);
            return;
        }

        setChannelModalData(null);
        const requestLifetime = createRequestLifetime();
        requestChannelCampaignData(new ProjectApi(getAuthorityConfiguration()), {
            projectId: project.id,
            channelId: channelModal.channel.id,
            campaignRemoteId: channelModal.campaign.remoteId,
            since: since.format('YYYY-MM-DD'),
            until: until.format('YYYY-MM-DD'),
            centresAllowListed,
            domainsAllowListed,
        })
            .then((result) => {
                requestLifetime.run(() => {
                    setChannelModalData(result);
                });
            })
            .catch((e) => {
                requestLifetime.run(() => {
                    et(e).then((text) => {
                        requestLifetime.run(() => {
                            setChannelModalData({error: text});
                        });
                    });
                });
            });

        return () => {
            requestLifetime.cancel();
        };
    }, [project.id, since.format('YYYY-MM-DD'), until.format('YYYY-MM-DD'), centresAllowListed, domainsAllowListed, channelModal, campaignBreakdownAllowed]);

    return [
        <Modal
            visible={channelModal !== null}
            setVisible={() => setChannelModal(null)}
            loading={channelModalData === null}
            title={channelModalData?.name}
            subtitle={channelModalData?.remoteId}
            key={'modal'}
        >
            {channelModalData?.error && <FormError error={channelModalData?.error}/>}
            <ChannelGridModalContent data={channelModalData} columns={columns}/>
        </Modal>,
        <LoadingComponent className={'col-span-12'} loading={channelList === null} key={'table'}>
            {showSnapshotStatus && <ReportingSnapshotStatus snapshots={snapshot} mediaSourceQuality={snapshot?.mediaSourceQuality}/>}
            {channelGridShowsPurchaseCostCoverage(gridType, metricContext) && <ReportingCostCoverageContext
                data={effectiveCollection?.partialProfitabilityMarketing}
                reportContext="marketing"
                loading={channelList === null}
            />}

            {channelList && visibleChannels.map((channel) => <ChannelGridSection
                key={channel.id}
                gridType={gridType}
                metricContext={metricContext}
                channel={{id: channel.id, name: channel.name}}
                channelData={channel.overview}
                setChannelTotal={setChannelTotal}
                setModalCampaignRow={channelAllowsCampaignBreakdown(channel, campaignBreakdownAllowed) ? (campaign) => {
                    setChannelModalData(null);
                    setChannelModal({
                        channel: channel,
                        campaign: campaign,
                    });
                } : undefined}
            />)}

            {showFullPeriodTotals && channelTotalResult.length > 0 && <GridSection
                title={t('project.total')}
                gridType={gridType}
            >
                {<DataGrid2
                    group="marketing-profitability"
                    dataSource={channelTotalResult}
                    columns={columns}
                    showHeader={true}
                    hasTotal={true}
                />}
            </GridSection>}
        </LoadingComponent>
    ];
}
